import { AlertModal, FontWeight, Heading, LiliButtonType } from '@lili/lili-frontend-common';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBalanceUpOptInMutation } from '../../../hooks/mutations/useBalanceUpOptInMutation';
import { useAccountSummaryQuery } from '../../../hooks/query/useAccountSummaryQuery';
import { useStore } from '../../../store';
import { BalanceUpDashboardAlert } from './BalanceUpDashboardAlert';
import BalanceUpDashboardFooter from './BalanceUpDashboardFooter';
import BalanceUpDonut from './BalanceUpDonut';
import { OverdraftDetails } from '../../../types';
import { convertValueToUSD } from '../../../helpers/currency.helper';

interface BalanceUpDashboardProps {
  overdraftObject: OverdraftDetails;
}
const BalanceUpDashboard = ({ overdraftObject }: BalanceUpDashboardProps) => {
  const { t } = useTranslation('balanceUpTranslation');
  const { getPrimaryAccount } = useStore((state) => state);
  const {
    customer: { externalId },
  } = useStore((state) => state.summary);

  const [percentage, setPercentage] = useState(0);

  const [isConfirmation, setIsConfirmation] = useState<boolean>(false);

  const { optIn, eligibilityLimit, eligibilityUsed, state, daysUntilRenewal, eligible: isEligible } = overdraftObject;

  const { refetch: updateSummary, isFetching: accountSummaryLoading } = useAccountSummaryQuery();

  const { mutate, isLoading: isLoadingOptIn } = useBalanceUpOptInMutation({
    customerId: externalId,
    achAccountNumber: getPrimaryAccount().achAccountNumber,
    optIn: !optIn,
    handleOnSuccess: () => {
      updateSummary();
    },
  });

  const handleOnToggle = () => {
    if (optIn) {
      setIsConfirmation(true);
    } else {
      mutate();
    }
  };

  useEffect(() => {
    const percent = ((eligibilityLimit - eligibilityUsed) / eligibilityLimit) * 100;
    setPercentage(percent);
  }, [overdraftObject]);

  const limitReached = eligibilityLimit - eligibilityUsed <= 0;

  return (
    <div className='flex flex-col gap-[24px] pc-md:p-[40px] p-[32px] pc-md:pb-[32px]'>
      {(limitReached || !isEligible) && (
        <BalanceUpDashboardAlert
          eligibilityUsed={eligibilityUsed}
          eligibilityLimit={eligibilityLimit}
          overdraftState={state}
        />
      )}
      {isConfirmation && (
        <AlertModal
          title={t('TURN_OFF_MODAL.TITLE')}
          description={t('TURN_OFF_MODAL.DESCRIPTION')}
          submitButtonTitle={t('TURN_OFF_MODAL.CTA')}
          cancelButtonTitle={t('TURN_OFF_MODAL.CANCEL')}
          onSubmit={() => {
            setIsConfirmation(false);
            mutate();
          }}
          onCancel={() => setIsConfirmation(false)}
          submitButtonType={LiliButtonType.PRIMARY}
        />
      )}
      <div className='flex flex-col items-center justify-center relative border-[1px] border-outline-primary-bw-03 rounded-[8px]'>
        <div className='flex flex-col items-center'>
          <BalanceUpDonut
            currencySign='$'
            currencyAmount={isEligible ? eligibilityLimit - eligibilityUsed : eligibilityUsed}
            percentage={percentage}
            disabled={!isEligible || !optIn || limitReached}
            danger={percentage <= 50}
            isEligible={isEligible}
          />
          <div
            className={`flex flex-row gap-[25px] mb-[23px] ${!optIn || !isEligible ? 'text-text-main-disabled' : 'text-text-main-tertiary'}`}
          >
            <div className='flex flex-col gap-[4px] items-center w-[80px]' id='balance-up-used-amount'>
              <Heading.H10 className='text-inherit'>{t('DASHBOARD.USED')}</Heading.H10>
              <Heading.H9
                fontWeight={FontWeight.MEDIUM}
                className={`${!optIn || !isEligible ? 'text-text-main-disabled' : 'text-text-main-primary'}`}
              >
                {`${convertValueToUSD(eligibilityUsed, 2)}`}
              </Heading.H9>
            </div>
            <div className='w-[1px] h-[44px] bg-surface-primary-bw-03' />
            <div className='flex flex-col gap-[4px] items-center w-[84px]' id='balance-up-eligibility-limit'>
              <Heading.H10 className='text-inherit'>{t('DASHBOARD.TOTAL_AMOUNT')}</Heading.H10>
              <Heading.H9
                fontWeight={FontWeight.MEDIUM}
                className={`${!optIn || !isEligible ? 'text-text-main-disabled' : 'text-text-main-primary'}`}
              >
                {`${convertValueToUSD(eligibilityLimit, 2)}`}
              </Heading.H9>
            </div>
          </div>
          {daysUntilRenewal && isEligible ? (
            <div className='flex flex-row gap-[4px] mb-[44px] items-baseline' id='balance-up-days-until-renewal'>
              <Heading.H9
                fontWeight={FontWeight.MEDIUM}
                className={!optIn || !isEligible ? 'text-text-main-disabled' : 'text-text-main-primary'}
              >
                {daysUntilRenewal}
              </Heading.H9>
              <Heading.H10 className={!optIn || !isEligible ? 'text-text-main-disabled' : 'text-text-main-tertiary'}>
                {t('DASHBOARD.DAYS_LEFT', { count: daysUntilRenewal })}
              </Heading.H10>
            </div>
          ) : (
            <div className='mb-[44px]' />
          )}
        </div>
        <BalanceUpDashboardFooter
          isEligible={isEligible}
          limitReached={limitReached}
          balanceUpState={optIn}
          handleOnToggle={handleOnToggle}
          isLoading={isLoadingOptIn || accountSummaryLoading}
        />
      </div>
    </div>
  );
};

export default BalanceUpDashboard;
